import { useState, useEffect } from "react";
import {
    Box,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
} from "@mui/material";
import CustomDialog from "components/CustomDialog";
import { apiUrl } from "config/settings";
import http from "http";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { useDispatch, useSelector } from "react-redux";
import { setShowLoading } from "store/sysSlice";
import moment from "moment";

const McBoostPower = ({ data, setData }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { bt } = useBizTypeTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const bizPath = useSelector((state) => state?.biz?.data?.path);
    const [selectedMinutes, setSelectedMinutes] = useState(60);
    const [nextTime, setNextTime] = useState(null)
    const managedSchedule = data?.data?.scheduleNextOn !== undefined;

    const options = [
        { value: 60, label: bt("ONE_HOUR") },
        { value: 120, label: bt("TWO_HOURS") },
        { value: 180, label: bt("THREE_HOURS") },
        { value: 240, label: bt("FOUR_HOURS") },
    ]

    useEffect(() => {
        if (data?.data?.scheduleNextOn !== undefined) {
            setNextTime(data.data.scheduleNextOn ? { nextRunText: moment(data.data.scheduleNextOn).format("DD/MM/YYYY HH:mm") } : null);
        } else setNextTime(getNextWeeklyStartTime(data?.data?.weeklyTime))
    }, [data]);

    const onClose = () => {
        setData(false);
        setSelectedMinutes(60);
    };

    const onSubmit = () => {
        if (!selectedMinutes) return;

        dispatch(setShowLoading(true));

        http
            .patch(`${apiUrl}/mc/boostPower/${bizPath}/${data?.data?.guid}`, {
                boostPower: selectedMinutes,
            })
            .then(() => {
                dispatch(setShowLoading(false));
                onClose();
            })
            .catch(() => {
                dispatch(setShowLoading(false));
            });
    };

    const getNextWeeklyStartTime = (weeklyTime = []) => {
        const now = moment()

        const getDayNumber = (dayValue) => {
            if (!dayValue) return null;
            if (typeof dayValue === "number") return dayValue;

            const map = {
                SUN: 1,
                SUNDAY: 1,
                MON: 2,
                MONDAY: 2,
                TUE: 3,
                TUESDAY: 3,
                WED: 4,
                WEDNESDAY: 4,
                THU: 5,
                THURSDAY: 5,
                FRI: 6,
                FRIDAY: 6,
                SAT: 7,
                SATURDAY: 7,
            };

            return map[String(dayValue).toUpperCase()] ?? null;
        };

        const getTimePartsInIsrael = (value) => {
            if (!value) return null;

            const m = moment(value)
            if (!m.isValid()) return null;

            return {
                hour: m.hours(),
                minute: m.minutes(),
            };
        };

        let nextItem = null;
        const currentDay = now.day() + 1;

        for (const item of weeklyTime) {
            if (!item?.isActive || !item?.start) continue;

            const dayNum = getDayNumber(item.day);
            const timeParts = getTimePartsInIsrael(item.start);

            if (!dayNum || !timeParts) continue;

            let daysToAdd = dayNum - currentDay;
            if (daysToAdd < 0) {
                daysToAdd += 7;
            }

            const candidate = now.clone()
                .add(daysToAdd, "days")
                .hour(timeParts.hour)
                .minute(timeParts.minute)
                .second(0)
                .millisecond(0);

            if (candidate.isSameOrBefore(now)) {
                candidate.add(7, "days");
            }

            if (!nextItem || candidate.isBefore(nextItem.nextRunMoment)) {
                nextItem = {
                    sourceItem: item,
                    day: item.day,
                    start: item.start,
                    end: item.end ?? null,
                    nextRunMoment: candidate.clone(),
                    nextRunDate: candidate.toDate(),
                    nextRunText: candidate.format("DD/MM/YYYY HH:mm"),
                    nextRunDayName: candidate.format("dddd"),
                };
            }
        }

        return nextItem;
    };

    return (
        <CustomDialog
            open={data?.open}
            onClose={onClose}
            title={managedSchedule ? "CS_BOOST_TITLE" : "OUTSIDE_WORKING_HOURS"}
            width={isMobile ? "92%" : 380}
            actionBtn={{ title: 'CONTINUE', onClick: () => onSubmit() }}
            closeBtn="CANCEL"
        >
            <Stack spacing={3}>
                <Stack spacing={1}>
                    {nextTime && <Stack spacing={0.5}>

                        <Typography
                            textAlign="center"
                            color="success.main"
                            fontSize={isMobile ? 14 : 15}
                        >
                            {`${bt("NEXT_TIME_ON_SCHEDULE")}`}
                        </Typography>
                        <Typography
                            textAlign="center"
                            color="success.main"
                            fontSize={isMobile ? 14 : 15}
                        >
                            {`${nextTime?.nextRunText}`}
                        </Typography>
                    </Stack>}

                    <Divider />

                    <Typography
                        textAlign="center"
                        fontWeight="bold"
                        fontSize={isMobile ? 14 : 15}
                    >
                        {bt(managedSchedule ? "CS_BOOST_EXPLANATION" : "OUTSIDE_WORKING_HOURS_MSG")}
                    </Typography>

                    <Typography
                        textAlign="center"
                        fontSize={isMobile ? 14 : 15}
                    >
                        {bt(managedSchedule ? "CS_BOOST_RETURN" : "OUTSIDE_WORKING_HOURS_MSG_1")}
                    </Typography>
                </Stack>

                <Box display="flex" justifyContent="center">
                    <ToggleButtonGroup
                        value={selectedMinutes}
                        exclusive
                        onChange={(_, value) => {
                            if (value !== null) setSelectedMinutes(value);
                        }}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1.2,
                            width: "100%",
                            "& .MuiToggleButtonGroup-grouped": {
                                borderRadius: "12px !important",
                                border: `1px solid ${theme.palette.divider} !important`,
                                margin: "0 !important",
                            },
                        }}
                    >
                        {options.map((item) => (
                            <ToggleButton
                                key={item.value}
                                value={item.value}
                                sx={{
                                    py: 1.2,
                                    fontWeight: 700,
                                    fontSize: 15,
                                    textTransform: "none",
                                    backgroundColor:
                                        selectedMinutes === item.value
                                            ? theme.palette.primary.main
                                            : theme.palette.background.paper,
                                    color:
                                        selectedMinutes === item.value
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                    "&:hover": {
                                        backgroundColor:
                                            selectedMinutes === item.value
                                                ? theme.palette.primary.main
                                                : theme.palette.action.hover,
                                    },
                                }}
                            >
                                {item.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Stack>
        </CustomDialog>
    );
};

export default McBoostPower;
